import { type ServiceMappers, ServiceType } from '../../domain/types';
import { createCheckoutAPIClient } from '@/scopes/checkout/infrastructure/api/checkoutAPIClient';
import { createBookingDraftResponseMapper } from '@/scopes/checkout/infrastructure/api/bookingDraftResponseMapper';
import { createPerBookingServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/perBookingServiceMapper';
import { createPerParticipantServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/perParticipantServiceMapper';
import { createCarRentalServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/carRentalServiceMapper';
import { createCancellationInsuranceServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/cancellationInsuranceServiceMapper';
import { createPerRoomServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/perRoomServiceMapper';
import { createLuggageServiceMapper } from './service-mappers/luggageServiceMapper';
import { createBookingRedeemServiceMapper } from './service-mappers/bookingRedeemServiceMapper';
import { createSkiAncillaryServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/skiAncillaryServiceMapper';
import { createClothingServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/clothingServiceMapper';
import { createBusServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/busServiceMapper';
import { createCarRentalFilteringServiceMapper } from '@/scopes/checkout/infrastructure/api/service-mappers/carRentalFilteringServiceMapper';

export const serviceMappers: ServiceMappers = {
  [ServiceType.CarRental]: createCarRentalServiceMapper({ serviceName: 'AUTOHUUR*', serviceTypeMapper: 'carRental' }),
  [ServiceType.CarRentalFiltering]: createCarRentalFilteringServiceMapper({
    serviceName: 'AUTOHUUR*',
    serviceTypeMapper: 'carRentalFilters',
  }),
  [ServiceType.TravelInsurance]: createPerParticipantServiceMapper({
    serviceName: 'TRAVEL*',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.TravelInsurance,
  }),
  [ServiceType.TravelInsurancePerBooking]: createPerBookingServiceMapper({
    serviceName: 'TRAVEL*',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.TravelInsurancePerBooking,
  }),
  [ServiceType.CancellationInsurance]: createCancellationInsuranceServiceMapper({
    serviceName: 'CANCEL*',
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.CancellationInsurancePerBooking]: createPerBookingServiceMapper({
    serviceName: 'CANCEL*',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.CancellationInsurancePerBooking,
  }),
  [ServiceType.Transfer]: createPerBookingServiceMapper({
    serviceName: 'TRANSFER',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.Transfer,
  }),
  [ServiceType.Visa]: createPerBookingServiceMapper({
    serviceName: 'VISA*',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.Visa,
  }),
  [ServiceType.Rebooking]: createPerBookingServiceMapper({
    serviceName: 'REBOOKING',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.Rebooking,
  }),
  [ServiceType.Babycot]: createPerParticipantServiceMapper({
    serviceName: 'BABY*',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.Babycot,
  }),
  [ServiceType.MealplanDP]: createPerRoomServiceMapper({
    serviceName: 'ACCOMMODATION',
    serviceTypeMapper: 'mealplan',
    serviceType: ServiceType.MealplanDP,
  }),
  [ServiceType.Mealplan]: createPerRoomServiceMapper({
    serviceName: 'VERZORGING*',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.Mealplan,
  }),
  [ServiceType.MealplanExternal]: createPerRoomServiceMapper({
    serviceName: 'HALFBOARD*',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.MealplanExternal,
  }),
  [ServiceType.Breakfast]: createPerRoomServiceMapper({
    serviceName: 'BREAKFAST',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.Breakfast,
  }),
  [ServiceType.BreakfastArrivalBus]: createPerRoomServiceMapper({
    serviceName: 'BREAKFARRHINT_00',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.BreakfastArrivalBus,
  }),
  [ServiceType.Dinner]: createPerRoomServiceMapper({
    serviceName: 'DINNER',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.Dinner,
  }),
  [ServiceType.BookingRedeem]: createBookingRedeemServiceMapper({
    serviceName: 'REDEEM',
    serviceTypeMapper: 'redeem',
  }),
  [ServiceType.Luggage]: createLuggageServiceMapper({ serviceName: 'LUG_*', serviceTypeMapper: 'luggage' }),
  [ServiceType.SkiPass]: createSkiAncillaryServiceMapper({
    serviceName: 'SKI*',
    serviceType: ServiceType.SkiPass,
    serviceTypeMapper: 'pass',
  }),
  [ServiceType.SkiMaterial]: createSkiAncillaryServiceMapper({
    serviceName: 'SKI*',
    serviceType: ServiceType.SkiMaterial,
    serviceTypeMapper: 'material',
  }),
  [ServiceType.SkiLesson]: createSkiAncillaryServiceMapper({
    serviceName: 'SKI*',
    serviceType: ServiceType.SkiLesson,
    serviceTypeMapper: 'lesson',
  }),
  [ServiceType.Sweater]: createClothingServiceMapper({
    serviceName: 'SWEATER*',
    serviceType: ServiceType.Sweater,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.TShirt]: createClothingServiceMapper({
    serviceName: 'TSHIRT*',
    serviceType: ServiceType.TShirt,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.DutchweekValThorens]: createPerParticipantServiceMapper({
    serviceName: 'DUTCHWEEK1',
    serviceType: ServiceType.DutchweekValThorens,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.Dutchweek]: createPerParticipantServiceMapper({
    serviceName: 'DUTCHWEEK',
    serviceType: ServiceType.Dutchweek,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.Comedy]: createPerParticipantServiceMapper({
    serviceName: 'COMEDY',
    serviceType: ServiceType.Comedy,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.ParkingAirport]: createPerBookingServiceMapper({
    serviceName: 'PARKEREN',
    serviceType: ServiceType.ParkingAirport,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.SkiTransfer]: createPerParticipantServiceMapper({
    serviceName: 'SHAREDBUS',
    serviceType: ServiceType.SkiTransfer,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.SkiTransferPerBooking]: createPerBookingServiceMapper({
    serviceName: 'SHAREDBUS',
    serviceType: ServiceType.SkiTransferPerBooking,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.SkiCarriage]: createPerParticipantServiceMapper({
    serviceName: 'CARRIAGESKI*',
    serviceType: ServiceType.SkiCarriage,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.PartyTickets]: createPerParticipantServiceMapper({
    serviceName: 'PARTY',
    serviceType: ServiceType.PartyTickets,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.Bus]: createBusServiceMapper({
    serviceName: 'BUS',
    serviceTypeMapper: 'bus',
  }),
  [ServiceType.Golf]: createPerParticipantServiceMapper({
    serviceName: 'GREENFEE',
    serviceType: ServiceType.Golf,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.Bridge]: createPerParticipantServiceMapper({
    serviceName: 'BRIDGE',
    serviceType: ServiceType.Bridge,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.DinnerVouchers]: createPerBookingServiceMapper({
    serviceName: 'MEAL-VOUCHERS',
    serviceType: ServiceType.DinnerVouchers,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.GenericServicePerBooking]: createPerBookingServiceMapper({
    serviceName: 'GENERIC',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.GenericServicePerBooking,
  }),
  [ServiceType.GenericServicePerParticipant]: createPerParticipantServiceMapper({
    serviceName: 'GENERIC',
    serviceType: ServiceType.GenericServicePerParticipant,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.GenericServicePerRoom]: createPerRoomServiceMapper({
    serviceName: 'GENERIC',
    serviceType: ServiceType.GenericServicePerRoom,
    serviceTypeMapper: 'genericService',
  }),
  [ServiceType.ESim]: createPerParticipantServiceMapper({
    serviceName: 'ESIM',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.ESim,
  }),
  [ServiceType.BikeRental]: createPerParticipantServiceMapper({
    serviceName: 'FIETS',
    serviceTypeMapper: 'genericService',
    serviceType: ServiceType.BikeRental,
  }),
};

export const checkoutAPIClient = createCheckoutAPIClient({
  bookingDraftResponseMapper: createBookingDraftResponseMapper({ serviceMappers }),
  serviceMappers,
});
